import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ConfigurationRoute } from '@api-response-types/configuration.response';
import { IntervalType } from '@api-types/enums/interval-type.enum';
import { Resolution } from '@api-types/enums/resolution.enum';
import { FeatureFlagSelectors } from '@core/store/feature-flags/feature-flags.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { ForecastVersionHttpClient } from 'demand-forecasting/services/http/forecast-version-http-client.service';
import { ConfigurationsActions } from 'demand-forecasting/store/configurations/configurations.actions';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import { ConfigurationRouterHelperService } from 'demand-forecasting/store/router-state/configuration-router-helper.service';
import {
  selectConfigurationId,
  selectVersionId,
} from 'demand-forecasting/store/router-state/router.selectors';
import { ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  catchError,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { ForecastVersionActions } from './forecast-version.actions';
import {
  selectForecastVersionsByConfigurationUrlParam,
  selectGetAllForecastVersionsIsFetching,
} from './forecast-version.selectors';

@Injectable()
export class ForecastVersionEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly forecastVersionHttpClient = inject(
    ForecastVersionHttpClient,
  );
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastrService);
  private readonly dialogRef = inject(MatDialog);
  private readonly translateService = inject(TranslateService);

  private readonly configurationRouterHelperService = inject(
    ConfigurationRouterHelperService,
  );

  getForecastVersionsForConfiguration$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ForecastVersionActions.getAllForecastVersionsForCofiguration),
        withLatestFrom(
          this.store.select(selectGetAllForecastVersionsIsFetching),
          this.store.select(selectConfigurationId),
        ),
        tap(() =>
          this.store.dispatch(
            ForecastVersionActions.setGetAllForecastVersionsIsFetching({
              isFetching: true,
            }),
          ),
        ),
        switchMap(([, , configurationId]) => {
          if (!configurationId) {
            return [];
          }
          return this.forecastVersionHttpClient
            .getForecastVersions(configurationId)
            .pipe(
              map((forecastVersions) =>
                this.store.dispatch(
                  ForecastVersionActions.setAllForecastVersions({
                    forecastVersions,
                  }),
                ),
              ),
              catchError((error: HttpErrorResponse) => {
                Sentry.captureException(error, {
                  tags: {
                    feature: 'forecast-version',
                    operation: 'get-forecast-versions-for-configuration',
                  },
                  extra: {
                    configurationId,
                    statusCode: error.status,
                    errorMessage: error.message,
                  },
                });
                return EMPTY;
              }),
            );
        }),
        tap(() =>
          this.store.dispatch(
            ForecastVersionActions.setGetAllForecastVersionsIsFetching({
              isFetching: false,
            }),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  addForecastVersion$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ForecastVersionActions.addForecastVersion),
        map(({ forecastVersion }) => {
          this.store.dispatch(
            ConfigurationsActions.getConfiguration({
              configurationId: forecastVersion.configurationId,
            }),
          );
        }),
      ),
    { dispatch: false },
  );
  getForecastVersionById$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ForecastVersionActions.getForecastVersionById),
        // filter(([, isFetching]) => !isFetching),
        /* tap(() =>
          this.store.dispatch(
            ForecastVersionActions.setGetAllForecastVersionsIsFetching({
              isFetching: true,
            }),
          ),
        ), */
        exhaustMap((props) =>
          this.forecastVersionHttpClient
            .getForecastVersion(props.forecastVersionId)
            .pipe(
              map((forecastVersion) =>
                this.store.dispatch(
                  ForecastVersionActions.addForecastVersion({
                    forecastVersion,
                  }),
                ),
              ),
              catchError((error: HttpErrorResponse) => {
                Sentry.captureException(error, {
                  tags: {
                    feature: 'forecast-version',
                    operation: 'get-forecast-version-by-id',
                  },
                  extra: {
                    forecastVersionId: props.forecastVersionId,
                    statusCode: error.status,
                    errorMessage: error.message,
                  },
                });
                return EMPTY;
              }),
            ),
        ),
        /* tap(() =>
          this.store.dispatch(
            ForecastVersionActions.setGetAllForecastVersionsIsFetching({
              isFetching: false,
            }),
          ),
        ), */
      ),
    {
      dispatch: false,
    },
  );
  getForecastVersionsByIds$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ForecastVersionActions.getAllForecastVersionsByIds),
        withLatestFrom(
          this.store.select(selectGetAllForecastVersionsIsFetching),
        ),
        // TODO check if loading issues disappear after that

        // filter(([, isFetching]) => !isFetching),
        tap(() =>
          this.store.dispatch(
            ForecastVersionActions.setGetAllForecastVersionsIsFetching({
              isFetching: true,
            }),
          ),
        ),
        exhaustMap(([props]) =>
          this.forecastVersionHttpClient
            .getForecastVersionsByIds(props.forecastVersionIds)
            .pipe(
              map((forecastVersions) =>
                this.store.dispatch(
                  ForecastVersionActions.setAllForecastVersions({
                    forecastVersions,
                  }),
                ),
              ),
              catchError((error: HttpErrorResponse) => {
                Sentry.captureException(error, {
                  tags: {
                    feature: 'forecast-version',
                    operation: 'get-forecast-versions-by-ids',
                  },
                  extra: {
                    forecastVersionIds: props.forecastVersionIds,
                    statusCode: error.status,
                    errorMessage: error.message,
                  },
                });
                return EMPTY;
              }),
            ),
        ),
        tap(() =>
          this.store.dispatch(
            ForecastVersionActions.setGetAllForecastVersionsIsFetching({
              isFetching: false,
            }),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  deleteForecastVersion$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ForecastVersionActions.deleteForecastVersion),
        withLatestFrom(
          this.store.select(selectConfigurationId),
          this.store.select(selectForecastVersionsByConfigurationUrlParam),
          this.store.select(selectVersionId),
        ),
        tap(() => {
          this.store.dispatch(
            ForecastVersionActions.setDeleteForecastVersionIsPending({
              isPending: true,
            }),
          );
        }),
        switchMap(
          ([
            { versionId: toBeDeletedVersionId },
            configurationId,
            availableVersionsBeforeDelete,
            selectedVersionId,
          ]) => {
            if (
              !toBeDeletedVersionId ||
              !configurationId ||
              !selectedVersionId
            ) {
              return of();
            }

            return this.forecastVersionHttpClient
              .deleteResultSet(toBeDeletedVersionId)
              .pipe(
                tap(() => {
                  this.store.dispatch(
                    ForecastVersionActions.getAllForecastVersionsForCofiguration(
                      {
                        configurationId,
                      },
                    ),
                  );

                  const newAvailableVersions =
                    availableVersionsBeforeDelete.filter(
                      (version) => version.id !== toBeDeletedVersionId,
                    );

                  // Routing logic after delete
                  if (toBeDeletedVersionId === selectedVersionId) {
                    if (newAvailableVersions.length === 0) {
                      this.router.navigate(['demand/configurations']);
                    } else {
                      this.configurationRouterHelperService.updateVersionId(
                        newAvailableVersions[0].id,
                      );
                    }
                  }

                  this.dialogRef.closeAll();

                  // Success toast
                  this.toastService.info('Version erfolgreich gelöscht.');
                }),
                catchError((error: HttpErrorResponse) => {
                  Sentry.captureException(error, {
                    tags: {
                      feature: 'forecast-version',
                      operation: 'delete-forecast-version',
                    },
                    extra: {
                      toBeDeletedVersionId,
                      configurationId,
                      statusCode: error.status,
                      errorMessage: error.message,
                    },
                  });
                  this.toastService.error('Error deleting forecast version');
                  return EMPTY;
                }),
                finalize(() => {
                  this.store.dispatch(
                    ForecastVersionActions.setDeleteForecastVersionIsPending({
                      isPending: false,
                    }),
                  );
                }),
              );
          },
        ),
      ),
    {
      dispatch: false,
    },
  );

  createVersion$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ForecastVersionActions.createVersion),
        withLatestFrom(
          this.store.select(ConfigurationsSelectors.selectAllConfigurations),
          this.store.select(
            FeatureFlagSelectors.selectConfidenceIntervalDefaultSelected,
          ),
        ),
        tap(() => {
          this.store.dispatch(
            ForecastVersionActions.setCreateVersionIsPending({
              isPending: true,
            }),
          );
        }),
        switchMap(
          ([
            { configurationId, executeConfigurationDto },
            allConfigurations,
            confidenceIntervalDefaultSelected,
          ]) => {
            if (!configurationId) {
              return of();
            }

            return this.forecastVersionHttpClient
              .createVersion(configurationId, executeConfigurationDto)
              .pipe(
                map((res) => {
                  // Add the version to the store immediately, so that it is visible in the select.
                  this.store.dispatch(
                    ForecastVersionActions.addForecastVersion({
                      forecastVersion: res,
                    }),
                  );
                  // TODO only load/update the single configuration when on the forecast version page
                  /* this.store.dispatch(
                    ConfigurationsActions.getAllConfigurations(),
                  ); */
                  const configuration = allConfigurations.find(
                    (config) => res.configurationId === config.id,
                  );
                  /* if (configuration) {
                    this.configurationRouterHelperService.updateVersionId(
                      res.id,
                    );
                  } */

                  // case: we created a new configuration and want to navigate to the new version
                  const isOnConfigurationCreatePage =
                    this.router.routerState.snapshot.url.includes(
                      '/configurations/create',
                    );

                  if (isOnConfigurationCreatePage) {
                    const setRouteOptions: ConfigurationRoute = {
                      configurationId: res.configurationId,
                      resolution:
                        configuration?.startForecastConfig?.resolution ||
                        Resolution.Yearly,
                      additionalTimeSeries:
                        configuration?.defaultAdditionalTimeSeries,
                      filterCombinationIds: null,
                      versionId: res.id,
                      fixedYAxisZeroPoint: true,
                      targetUnit: configuration?.startForecastConfig.target,
                    };
                    if (confidenceIntervalDefaultSelected) {
                      setRouteOptions.confidenceLevel = 0.9;
                      setRouteOptions.intervalType = IntervalType.PREDICTION;
                    }
                    this.configurationRouterHelperService.setInitialConfigurationRoute(
                      setRouteOptions,
                    );
                    // Note: we dont want to show success toast when we created a new configuration, therefore the else block is needed
                  } else {
                    this.toastService.success(
                      this.translateService.instant(
                        'UTILITY.NEW_VERSION_CREATED_SUCCESSFULLY',
                      ),
                    );
                  }
                }),
                catchError((error: HttpErrorResponse) => {
                  Sentry.captureException(error, {
                    tags: {
                      feature: 'forecast-version',
                      operation: 'create-version',
                    },
                    extra: {
                      configurationId,
                      statusCode: error.status,
                      errorMessage: error.message,
                    },
                  });
                  this.toastService.error(
                    this.translateService.instant(
                      'RESULT_OVERVIEW.RE_EXECUTE.ERROR.BODY',
                    ),
                    this.translateService.instant(
                      'RESULT_OVERVIEW.RE_EXECUTE.ERROR.TITLE',
                    ),
                  );
                  return EMPTY;
                }),
                finalize(() => {
                  this.store.dispatch(
                    ForecastVersionActions.setCreateVersionIsPending({
                      isPending: false,
                    }),
                  );
                }),
              );
          },
        ),
      ),
    {
      dispatch: false,
    },
  );

  updateForecastVersion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForecastVersionActions.updateForecastVersion),
      withLatestFrom(this.store.select(selectConfigurationId)),
      mergeMap(
        ([{ forecastVersionId, updateForecastVersionDto }, configurationId]) =>
          this.forecastVersionHttpClient
            .updateForecastVersion(forecastVersionId, updateForecastVersionDto)
            .pipe(
              map(() => {
                this.toastService.success(
                  this.translateService.instant(
                    'RESULT.UPDATE_FORECAST_VERSION.SUCCESS.BODY',
                  ),
                  this.translateService.instant(
                    'RESULT.UPDATE_FORECAST_VERSION.SUCCESS.TITLE',
                  ),
                );

                // TODO ideally we would only update the single forecast version in the store,
                //  but somehow this does not work.
                //  The same issue is found in the updateConfiguration$ effect
                //  Optimize this with PM-2127
                /*
                      ForecastVersionActions.patchForecastVersion({
                        forecastVersion,
                      }),
                  */
                if (!configurationId) {
                  throw new Error(
                    'Cannot get all forecast versions for configuration without forecast configuration id. This error should not happen',
                  );
                }
                return ForecastVersionActions.getAllForecastVersionsForCofiguration(
                  {
                    configurationId,
                  },
                );
              }),
              catchError((error: HttpErrorResponse) => {
                Sentry.captureException(error, {
                  tags: {
                    feature: 'forecast-version',
                    operation: 'update-forecast-version',
                  },
                  extra: {
                    forecastVersionId,
                    configurationId,
                    statusCode: error.status,
                    errorMessage: error.message,
                  },
                });
                this.toastService.error(
                  this.translateService.instant(
                    'RESULT.UPDATE_FORECAST_VERSION.ERROR.BODY',
                  ),
                  this.translateService.instant(
                    'RESULT.UPDATE_FORECAST_VERSION.ERROR.TITLE',
                  ),
                );
                return of(ForecastVersionActions.emptyAction());
              }),
            ),
      ),
    ),
  );
}
